import { useLocale } from 'next-intl';
import { FetchForgetPasswordRequest, SecurityQuestionData, SecurityQuestionDataResponse } from '@/types/forget-password';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useSiteStore } from '@/store/site';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { convertApiLocale } from '@nf/utils-common/site';
import { useEffect, useMemo, useState } from 'react';
import { ApiResponse } from '@nf/types/api';
import { Maybe } from '@nf/utils-common/fpw/monadic';

export const useForgetPassword = (canInitData: boolean) => {
	const apiDomain = useSiteStore((state) => state.apiDomain);
	// 請求太多次的資訊存取在token當中
	const [forgetPasswordToken, setForgetPasswordToken] = useState('');
	const locale = useLocale();
	const [securityQuestions, setSecurityQuestions] = useState<SecurityQuestionData[]>([]);
	const convertedApiLocale = useMemo(() => convertApiLocale(locale), [locale]);

	async function fetchForgetPasswordInitData (language: string, domain: string) {
		try {
			const response = await fetchComposer.postWithBearer(cloudUrlParser(`${domain}/ForgetPassword/GetInitData`), { body: JSON.stringify({ language }) })(forgetPasswordToken);
			Maybe(response.headers.get('X-Jwtoken'))
				.fold(
					() => {}, 
					(token: string) => setForgetPasswordToken(token)
				);
			const data: ApiResponse<{SecurotyQuestionData: SecurityQuestionDataResponse[]}> =  await response.json();
			const securityQuestionList = data?.Data?.SecurotyQuestionData;

			if (data.ErrorCode == 0 && securityQuestionList && securityQuestionList.length > 0) {
				setSecurityQuestions(securityQuestionList.map(question => ({
					label: question.Text,
					value: question.Value
				})));
			} else {
				throw new Error();
			}
		} catch (e) {
			throw e;
		}
	}

	useEffect(() => {
		canInitData && securityQuestions.length === 0 && apiDomain.ctcdDomain && convertedApiLocale && fetchForgetPasswordInitData(convertedApiLocale, apiDomain.ctcdDomain);
	}, [canInitData, apiDomain.ctcdDomain, convertedApiLocale, securityQuestions.length]);

	async function changeForgetPassword({
		email,
		SecurityQuestion,
		SecurityAnswer,
	}: Omit<FetchForgetPasswordRequest, 'language'>) {
		try {
			const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/ForgetPassword/Submit`), {
				body: JSON.stringify({
					email,
					SecurityQuestion,
					SecurityAnswer,
					language: convertedApiLocale,
				}),
			})(forgetPasswordToken);
			if (!response.ok) {
				throw new Error(`Forget password API failed with status ${response.status}`);
			}
			Maybe(response.headers.get('X-Jwtoken'))
				.fold(
					() => {}, 
					(token: string) => setForgetPasswordToken(token)
				);
			const data: ApiResponse = await response.json();
			if (data.ErrorCode === 0) {
				return true;
			} else {
				throw data
			}
		} catch (e) {
			throw e
		}
	}

	return {
		forgetPasswordToken,
		securityQuestions,
		changeForgetPassword,
	}
}
